import { useMixpanel } from '@/context/mixpanel'
import { MINUTE_IN_MILLISECONDS, SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { KeyboardEvent } from 'react'
import { useCallback, useEffect, useRef } from 'react'

export const SEARCH_IDLE_TIMEOUT = 2 * SECOND_IN_MILLISECONDS
export const SEARCH_REPEAT_WINDOW = 5 * MINUTE_IN_MILLISECONDS
const MIN_QUERY_LENGTH = 2

type SearchTrigger = 'idle' | 'enter' | 'blur' | 'leave'

interface UseSearchTrackingParams {
  resultsCount: number
  trackingParams?: Record<string, string>
}

const lastTrackedAt = new Map<string, number>()

const normalizeSearchQuery = (query: string) => query.trim().toLowerCase().replace(/\s+/g, ' ')

export const useSearchTracking = ({ resultsCount, trackingParams }: UseSearchTrackingParams) => {
  const { trackEvent } = useMixpanel()
  const pendingQuery = useRef<string | null>(null)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const latest = useRef({ resultsCount, trackingParams, trackEvent })
  latest.current = { resultsCount, trackingParams, trackEvent }

  const flush = useCallback((trigger: SearchTrigger) => {
    clearTimeout(idleTimer.current)
    const query = pendingQuery.current
    pendingQuery.current = null
    if (query === null) return

    const { resultsCount, trackingParams, trackEvent } = latest.current
    const dedupeKey = `${trackingParams?.Page ?? ''}|${query}`
    const now = Date.now()
    const previous = lastTrackedAt.get(dedupeKey)
    if (previous !== undefined && now - previous < SEARCH_REPEAT_WINDOW) return

    lastTrackedAt.set(dedupeKey, now)
    trackEvent('Search performed', { Query: query, ResultsCount: resultsCount, Trigger: trigger, ...trackingParams })
  }, [])

  const onSearchTyped = useCallback(
    (value: string) => {
      clearTimeout(idleTimer.current)
      const query = normalizeSearchQuery(value)
      if (query.length < MIN_QUERY_LENGTH) {
        pendingQuery.current = null
        return
      }
      pendingQuery.current = query
      idleTimer.current = setTimeout(() => flush('idle'), SEARCH_IDLE_TIMEOUT)
    },
    [flush]
  )

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') flush('enter')
    },
    [flush]
  )

  const onSearchBlur = useCallback(() => flush('blur'), [flush])

  useEffect(() => () => flush('leave'), [flush])

  return { onSearchTyped, onSearchKeyDown, onSearchBlur }
}
